// Public API of the mobile billing module. Components consume `useBilling()`;
// raw functions live here for places that need them outside React (e.g. the
// IAP transaction listener).
//
// Path convention: `config.billingApi.endpoint` already includes the `/billing`
// basePath of the API Gateway custom domain (set in mobile/serverless.yml as
// EXPO_PUBLIC_BILLING_API_ENDPOINT). The relative paths below intentionally
// do NOT repeat `/billing/` — that mirrors how clientApi paths don't repeat
// `/client/`.

import { billingGet, billingPost, billingDelete } from './api';
import type {
  BillingPlan,
  BillingCurrent,
  BillingPaymentMethod,
  BillingTransaction,
  PurchaseInitiateResult,
  PurchaseFinalizeResult,
  CancelResult,
  RestoreResult,
  BillingCoupon,
} from './types';
import { getStoreState } from '../../store/actions/_sessionIds';

function getIds() {
  const state = getStoreState();
  const account = state.session?.account;
  const user = state.authentication?.user;
  return {
    accountId: account?.account_id as string | undefined,
    cognitoId: user?.cognito_id as string | undefined,
  };
}

const requireIds = () => {
  const { accountId, cognitoId } = getIds();
  if (!accountId || !cognitoId) throw new Error('Not authenticated.');
  return { accountId, cognitoId };
};

// ─── Plans ───────────────────────────────────────────────────────────────────

export async function fetchAvailablePlans(): Promise<{ platform: string | null; plans: BillingPlan[] }> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingGet(`/plans/${accountId}/${cognitoId}`);
  if (!res?.success) throw new Error(res?.message || 'Could not load plans.');
  return res.payload;
}

// ─── Subscription ────────────────────────────────────────────────────────────

export async function fetchCurrentSubscription(): Promise<BillingCurrent> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingGet(`/subscription/${accountId}/${cognitoId}`);
  if (!res?.success) throw new Error(res?.message || 'Could not load subscription.');
  return res.payload;
}

export async function purchaseInitiate(productId: string): Promise<PurchaseInitiateResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/subscription/purchase/initiate/${accountId}/${cognitoId}`, { productId });
  if (!res?.success) {
    // Preserve structured failure shape (errorCode + payload) so the IAP /
    // UI layers can branch on specific server-side rejections (e.g.
    // EXISTING_STRIPE_SUBSCRIPTION → prompt user to cancel web sub first).
    const err = new Error(res?.message || 'Could not start purchase.') as Error & {
      errorCode?: string;
      payload?: any;
      status?: number;
    };
    err.errorCode = res?.errorCode;
    err.payload = res?.payload;
    err.status = res?.status;
    throw err;
  }
  return res.payload;
}

export async function purchaseFinalizeApple(signedTransactionInfo: string): Promise<PurchaseFinalizeResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/subscription/purchase/finalize/${accountId}/${cognitoId}`, { signedTransactionInfo });
  if (!res?.success) throw new Error(res?.message || 'Could not finalize purchase.');
  return res.payload;
}

export async function purchaseFinalizeStripe(input: { productId: string; customerId: string; discountCode?: string }): Promise<PurchaseFinalizeResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/subscription/purchase/finalize/${accountId}/${cognitoId}`, input);
  if (!res?.success) throw new Error(res?.message || 'Could not finalize purchase.');
  return res.payload;
}

export async function cancelSubscription(cancelAtPeriodEnd = true): Promise<CancelResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/subscription/cancel/${accountId}/${cognitoId}`, { cancelAtPeriodEnd });
  if (!res?.success) throw new Error(res?.message || 'Could not cancel.');
  return res.payload;
}

export async function restorePurchases(transactions: string[]): Promise<RestoreResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/subscription/restore/${accountId}/${cognitoId}`, { transactions });
  if (!res?.success) throw new Error(res?.message || 'Could not restore.');
  return res.payload;
}

// ─── Plans count (extras) ────────────────────────────────────────────────────
//
// POST /subscription/plans/set adjusts the customer's plan-slot count by
// updating the Stripe quantity. For Apple-primary accounts buying their
// first extras, the server responds with HTTP 402 + a SetupIntent client
// secret so the client can drive PaymentSheet, then retry with the captured
// paymentMethodId. Subsequent quantity changes on a customer who already has
// a Stripe extras sub need no PaymentSheet — the existing default PM is used.
//
// Response shape:
//   - HTTP 200: { count, changed, ... }
//   - HTTP 402: { errorCode: 'NEEDS_PAYMENT_METHOD',
//                payload: { setupIntent: { clientSecret, customerId } } }
//   - HTTP 400/403: { errorCode, message }
export interface SetPlansSuccess {
  ok: true;
  count: number;
  changed: boolean;
}
export interface SetPlansNeedsPayment {
  ok: false;
  needsPaymentMethod: true;
  setupIntent: { clientSecret: string; customerId: string };
}
export interface SetPlansError {
  ok: false;
  needsPaymentMethod: false;
  errorCode?: string;
  message: string;
}
export type SetPlansResult = SetPlansSuccess | SetPlansNeedsPayment | SetPlansError;

export async function setPlans(count: number, paymentMethodId?: string): Promise<SetPlansResult> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(
    `/subscription/plans/set/${accountId}/${cognitoId}`,
    paymentMethodId ? { count, paymentMethodId } : { count },
  );
  if (res?.success) {
    return { ok: true, count: res.payload?.count ?? count, changed: !!res.payload?.changed };
  }
  if (res?.errorCode === 'NEEDS_PAYMENT_METHOD' && res?.payload?.setupIntent?.clientSecret) {
    return {
      ok: false,
      needsPaymentMethod: true,
      setupIntent: {
        clientSecret: res.payload.setupIntent.clientSecret,
        customerId: res.payload.setupIntent.customerId,
      },
    };
  }
  return {
    ok: false,
    needsPaymentMethod: false,
    errorCode: res?.errorCode,
    message: res?.message || 'Could not change plan count.',
  };
}

// ─── Payment methods ─────────────────────────────────────────────────────────

export async function listPaymentMethods(): Promise<{ paymentMethods: BillingPaymentMethod[]; managedByApple?: boolean }> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingGet(`/payment-methods/${accountId}/${cognitoId}`);
  if (!res?.success) throw new Error(res?.message || 'Could not load payment methods.');
  return res.payload;
}

export async function addPaymentMethod(paymentMethodId: string, makeDefault = true): Promise<void> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/payment-methods/${accountId}/${cognitoId}`, { paymentMethodId, makeDefault });
  if (!res?.success) throw new Error(res?.message || 'Could not add payment method.');
}

export async function removePaymentMethod(paymentMethodId: string): Promise<void> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingDelete(`/payment-methods/${paymentMethodId}/${accountId}/${cognitoId}`);
  if (!res?.success) throw new Error(res?.message || 'Could not remove payment method.');
}

// ─── Transactions ────────────────────────────────────────────────────────────

export async function listTransactions(): Promise<BillingTransaction[]> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingGet(`/transactions/${accountId}/${cognitoId}`);
  if (!res?.success) throw new Error(res?.message || 'Could not load transactions.');
  return res.payload?.transactions || [];
}

// ─── Coupons ─────────────────────────────────────────────────────────────────

export async function verifyCoupon(code: string): Promise<{ coupon: BillingCoupon }> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/coupons/verify/${accountId}/${cognitoId}`, { code });
  if (!res?.success) throw new Error(res?.message || 'Invalid code.');
  return res.payload;
}

export async function applyCoupon(code: string): Promise<void> {
  const { accountId, cognitoId } = requireIds();
  const res = await billingPost(`/coupons/apply/${accountId}/${cognitoId}`, { code });
  if (!res?.success) throw new Error(res?.message || 'Could not apply coupon.');
}

export type {
  BillingDiscount,
  BillingPlan,
  BillingCurrent,
  BillingPaymentMethod,
  BillingTransaction,
  BillingCoupon,
  CancelResult,
  PurchaseInitiateResult,
  PurchaseFinalizeResult,
  RestoreResult,
} from './types';
